import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Calendar, MapPin, Clock, Users, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";

export default function VolunteerSignup() {
  const { ptaId, eventId } = useParams();
  const [ptaInfo, setPtaInfo] = useState<{ id: string; name: string; logo_url: string | null } | null>(null);
  const [eventInfo, setEventInfo] = useState<{ id: string; name: string; event_date: string; time: string | null; location: string | null } | null>(null);
  const [activeEvents, setActiveEvents] = useState<{ id: string; name: string; event_date: string; time: string | null; location: string | null }[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role_task: ""
  });
  
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  
  const generateMathQuestion = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaAnswer("");
  };
  
  useEffect(() => {
    generateMathQuestion();
  }, []);

  useEffect(() => {
    if (ptaId) {
      fetchPtaAndEvent();
    }
  }, [ptaId, eventId]);

  const fetchPtaAndEvent = async () => {
    const { data: ptaData } = await supabase
      .rpc('get_pta_by_short_code', { short_code: ptaId });

    if (ptaData && ptaData.length > 0) {
      setPtaInfo(ptaData[0]);
      
      if (eventId) {
        // Direct event link - fetch specific event
        const { data: eventData } = await supabase
          .from('events')
          .select('id, name, event_date, time, location')
          .eq('id', eventId)
          .eq('pta_id', ptaData[0].id)
          .eq('status', 'active')
          .eq('is_archived', false)
          .maybeSingle();

        setEventInfo(eventData);
      } else {
        // Generic PTA link - fetch all active events
        const { data: eventsData } = await supabase
          .from('events')
          .select('id, name, event_date, time, location')
          .eq('pta_id', ptaData[0].id)
          .eq('status', 'active')
          .eq('is_archived', false)
          .order('event_date', { ascending: true });

        setActiveEvents(eventsData || []);
      }
    }
    setLoading(false);
  };

  // The displayed event is either from direct link or user selection
  const displayedEvent = eventInfo || activeEvents.find(e => e.id === selectedEventId) || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayedEvent || !ptaInfo) return;
    
    if (parseInt(captchaAnswer) !== mathQuestion.answer) {
      toast.error("Incorrect answer. Please try again.");
      generateMathQuestion();
      return;
    }

    setSubmitting(true);

    const { error } = await supabase
      .from('event_helpers')
      .insert({
        event_id: displayedEvent.id,
        pta_id: ptaInfo.id,
        volunteer_name: formData.name,
        volunteer_email: formData.email,
        volunteer_phone: formData.phone || null,
        role_task: formData.role_task || null
      });

    if (error) {
      console.error(error);
      toast.error("Failed to sign up. Please try again.");
      generateMathQuestion();
    } else {
      setSubmitted(true);
      toast.success("Thank you for volunteering!");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!ptaInfo || (!eventInfo && !eventId && activeEvents.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-semibold mb-2">Not Found</h2>
            <p className="text-muted-foreground">This volunteer sign-up link is invalid, the event may have ended, or it has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!ptaInfo || (eventId && !eventInfo)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-semibold mb-2">Not Found</h2>
            <p className="text-muted-foreground">This volunteer sign-up link is invalid, the event may have ended, or it has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Thank You!</h2>
            <p className="text-muted-foreground">
              You have successfully signed up to volunteer for <strong>{displayedEvent?.name}</strong>. The PTA team will be in touch soon with more details.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-lg mx-auto space-y-6">
        <div className="text-center pt-8">
          {ptaInfo.logo_url && (
            <img 
              src={ptaInfo.logo_url} 
              alt={ptaInfo.name}
              className="h-16 w-auto mx-auto mb-4"
            />
          )}
          <h1 className="text-2xl font-bold">{ptaInfo.name}</h1>
          <p className="text-muted-foreground">Volunteer Sign-Up</p>
        </div>

        {/* Event selector when no eventId in URL */}
        {!eventId && activeEvents.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <Label className="mb-2 block">Select an Event</Label>
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an event to volunteer for..." />
                </SelectTrigger>
                <SelectContent>
                  {activeEvents.map(evt => (
                    <SelectItem key={evt.id} value={evt.id}>
                      {evt.name} — {format(new Date(evt.event_date), 'MMM d, yyyy')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Event Info Card */}
        {displayedEvent && (
          <>
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  {displayedEvent.name}
                </h2>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(displayedEvent.event_date), 'EEEE, MMMM d, yyyy')}
                  </p>
                  {displayedEvent.time && (
                    <p className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {displayedEvent.time}
                    </p>
                  )}
                  {displayedEvent.location && (
                    <p className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {displayedEvent.location}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Sign Up to Help
                </CardTitle>
                <CardDescription>
                  We'd love your help at this event! Fill out the form below to volunteer.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name *</Label>
                    <Input 
                      id="name" 
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input 
                      id="email" 
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (optional)</Label>
                    <Input 
                      id="phone" 
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Your phone number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">Preferred Role/Task (optional)</Label>
                    <Input 
                      id="role" 
                      value={formData.role_task}
                      onChange={(e) => setFormData({ ...formData, role_task: e.target.value })}
                      placeholder="e.g., BBQ, Face Painting, Ticket Sales"
                    />
                  </div>

                  {/* Math Captcha */}
                  <div className="space-y-2 p-3 bg-muted/50 rounded-lg border">
                    <Label htmlFor="captcha">Security Question *</Label>
                    <p className="text-sm text-muted-foreground">
                      What is {mathQuestion.num1} + {mathQuestion.num2}?
                    </p>
                    <Input 
                      id="captcha" 
                      type="number"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      placeholder="Enter your answer"
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={submitting || !captchaAnswer}
                  >
                    {submitting ? "Signing up..." : "Sign Up to Volunteer"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Powered by PTA Hub
        </p>
      </div>
    </div>
  );
}
